/**
 * Spago integration card — Plan-SpagoIntegration.md §F.
 *
 * One connect form installs Spago's reviewed connector package and registers
 * the identity key in the same step. Package rows are hidden from the custom
 * connector list and controlled here instead (on/off only).
 *
 * 🚨 Secrets (API key, identity secret) live only in password inputs, are sent
 * once, cleared after every attempt, and are never logged or shown back — the
 * status payload never returns them.
 *
 * Every API error `detail` is a plain string and is shown inline, never as a
 * toast. The name users see is "Spago", never "Spago Marketplace".
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle, AlertTriangle, CheckCircle2, KeyRound, LayoutGrid, Loader2,
  Plug, RefreshCw, Save, Store, ToggleLeft, ToggleRight, Unplug, UserCheck, X,
} from 'lucide-react';
import { spagoIntegrationAPI } from '../../services/api';
import type {
  SpagoEnvironment, SpagoHandmadeConnector, SpagoIntegrationStatus, SpagoUpdateBody,
} from '../../services/api';
import type { Agent } from '../../types';
import usePermissions from '../../hooks/usePermissions';
import ConfirmModal from '../ConfirmModal';
import { activeHandmadeConnectors } from '../../utils/spagoIntegration';

interface SpagoIntegrationCardProps {
  /** The page's agent list. `Agent.id` is the agent `_id` the contract uses. */
  agents: Agent[];
  /** Package rows / hand-made rows changed — the page reloads its connectors. */
  onConnectorsChanged: () => void;
  /** Connect / replace / disconnect can add, replace or remove the identity key.
   *  Optional since 2026-09-16: the page no longer lists keys separately, so
   *  there is usually nothing to refresh. */
  onIdentityKeysChanged?: () => void;
}

type FormMode = 'connect' | 'replace';

const CARD_LABELS: Record<'product_cards' | 'order_cards', string> = {
  product_cards: 'Product cards',
  order_cards: 'Order cards',
};

// Verbatim from Spago's review.
const KEY_HELP = [
  'Use your newest Spago API key that has Orders or Customers access. The Spago storefront signs buyer identity with it. If you create another such key later, update the key, Key ID and identity secret here.',
  "Key ID and identity secret: Spago Admin → API Keys ('Show identity secret').",
  'Create the key with all four scopes: Products, Inventory, Orders and Customers.',
  'To cut a key off, revoke it in Spago first.',
];

function errorDetail(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
}

// Every Spago route answers a bare 404 for any workspace except the one Spago
// workspace (backend `SPAGO_WORKSPACE_ID`). That means "not for this
// workspace", not a failure: the card renders nothing at all.
function isNotFound(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 404;
}

function warningsOf(status: SpagoIntegrationStatus): string[] {
  return Array.isArray(status.warnings) ? status.warnings : [];
}

const WarningNotices: React.FC<{ warnings: string[] }> = ({ warnings }) => (
  <div className="space-y-2">
    {warnings.map((w, i) => (
      <div
        key={i}
        role="status"
        className="flex gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300"
      >
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <p className="min-w-0">{w}</p>
      </div>
    ))}
  </div>
);

const AgentChecklist: React.FC<{
  agents: Agent[];
  selected: string[];
  onChange: (next: string[]) => void;
}> = ({ agents, selected, onChange }) => (
  <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 space-y-2 max-h-48 overflow-y-auto bg-white dark:bg-gray-900">
    {agents.length === 0 ? (
      <p className="text-xs text-gray-500 dark:text-gray-400">No agents yet.</p>
    ) : agents.map((a) => (
      <label key={a.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer min-w-0">
        <input
          type="checkbox"
          className="rounded shrink-0"
          checked={selected.includes(a.id)}
          onChange={() => onChange(
            selected.includes(a.id) ? selected.filter((id) => id !== a.id) : [...selected, a.id],
          )}
        />
        <span className="truncate">{a.config?.agent_name || a.agent_id}</span>
      </label>
    ))}
  </div>
);

const SpagoIntegrationCard: React.FC<SpagoIntegrationCardProps> = ({
  agents, onConnectorsChanged, onIdentityKeysChanged = () => {},
}) => {
  const { can } = usePermissions();
  const canView = can('integrations', 'view');
  const canAdd = can('integrations', 'add');
  const canEdit = can('integrations', 'edit');
  const canDelete = can('integrations', 'delete');

  const [status, setStatus] = useState<SpagoIntegrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  // Form (connect / replace key)
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [environment, setEnvironment] = useState<SpagoEnvironment | ''>('');
  const [apiKey, setApiKey] = useState('');
  const [keyId, setKeyId] = useState('');
  const [identitySecret, setIdentitySecret] = useState('');
  const [buyerIds, setBuyerIds] = useState<string[]>([]);
  const [catalogScope, setCatalogScope] = useState<'all' | 'selected'>('all');
  const [catalogIds, setCatalogIds] = useState<string[]>([]);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Non-null ⇒ the form is in its success state, showing these warnings.
  const [formWarnings, setFormWarnings] = useState<string[] | null>(null);

  // Connected-state actions
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [handmadeTarget, setHandmadeTarget] = useState<SpagoHandmadeConnector | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [removeKey, setRemoveKey] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await spagoIntegrationAPI.status();
      setStatus(data);
      setLoadError(null);
    } catch (err) {
      if (isNotFound(err)) {
        setUnavailable(true);
      } else {
        setLoadError(errorDetail(err, "Couldn't load the Spago connection."));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  // Nothing renders until the server has answered for THIS workspace — not even
  // a loading shell — so no workspace but the Spago one ever sees a Spago card.
  if (!canView || unavailable || (loading && !status && !loadError)) return null;

  const agentName = (id: string) => {
    const a = agents.find((x) => x.id === id);
    return a ? (a.config?.agent_name || a.agent_id) : 'Unknown agent';
  };
  const environmentLabel = (id: SpagoEnvironment | null) =>
    status?.environments.find((e) => e.id === id)?.label ?? id ?? '—';

  // ── form ────────────────────────────────────────────────────────────────

  const clearSecrets = () => {
    setApiKey('');
    setIdentitySecret('');
  };

  const closeForm = () => {
    setFormMode(null);
    clearSecrets();
    setKeyId('');
    setBuyerIds([]);
    setCatalogScope('all');
    setCatalogIds([]);
    setFormError(null);
    setFormWarnings(null);
  };

  const openConnect = () => {
    closeForm();
    const firstAvailable = status?.environments.find((e) => e.available)?.id ?? '';
    setEnvironment(firstAvailable);
    setFormMode('connect');
  };

  const openReplace = () => {
    if (!status) return;
    closeForm();
    setEnvironment(status.environment ?? '');
    setKeyId(status.key_id ?? '');
    setBuyerIds(status.buyer_agent_ids);
    setCatalogScope(status.catalog_agent_scope);
    setCatalogIds(status.catalog_agent_ids);
    setFormMode('replace');
  };

  const trimmedKey = apiKey.trim();
  const trimmedKid = keyId.trim();
  const trimmedSecret = identitySecret.trim();
  const selectedEnvAvailable = !!status?.environments.find((e) => e.id === environment && e.available);
  const canSubmit = formMode === 'connect'
    ? !!trimmedKey && !!trimmedKid && !!trimmedSecret && buyerIds.length > 0 && selectedEnvAvailable
    : buyerIds.length > 0;

  const handleSubmit = async () => {
    if (!formMode || !canSubmit) return;
    if (catalogScope === 'selected' && catalogIds.length === 0) {
      setFormError('Choose at least one catalog agent, or pick All agents.');
      return;
    }
    const catalog = catalogScope === 'all' ? null : catalogIds;
    let body: SpagoUpdateBody | null = null;
    if (formMode === 'replace') {
      body = { buyer_agent_ids: buyerIds, catalog_agent_ids: catalog };
      // The API key, Key ID and identity secret belong to ONE Spago key, so
      // they are replaced together or not at all.
      const replacingKey = !!trimmedKey || !!trimmedSecret || trimmedKid !== (status?.key_id ?? '');
      if (replacingKey) {
        if (!trimmedKey || !trimmedKid || !trimmedSecret) {
          setFormError('To replace the key, fill in the Spago API key, Key ID and identity secret together.');
          return;
        }
        body.api_key = trimmedKey;
        body.key_id = trimmedKid;
        body.identity_secret = trimmedSecret;
      }
    }
    setFormSaving(true);
    setFormError(null);
    try {
      const { data } = formMode === 'connect'
        ? await spagoIntegrationAPI.connect({
            environment: environment as SpagoEnvironment,
            api_key: trimmedKey,
            key_id: trimmedKid,
            identity_secret: trimmedSecret,
            buyer_agent_ids: buyerIds,
            catalog_agent_ids: catalog,
          })
        : await spagoIntegrationAPI.update(body ?? undefined);
      setStatus(data);
      setLoadError(null);
      onConnectorsChanged();
      onIdentityKeysChanged();
      const warnings = warningsOf(data);
      if (warnings.length > 0) {
        clearSecrets();
        setFormWarnings(warnings);
      } else {
        closeForm();
      }
    } catch (err) {
      // Non-secret fields stay filled; only the secrets are cleared.
      setFormError(errorDetail(
        err,
        formMode === 'connect' ? "Couldn't connect Spago." : "Couldn't save the changes.",
      ));
    } finally {
      clearSecrets();
      setFormSaving(false);
    }
  };

  // ── connected-state actions ───────────────────────────────────────────────

  const runAction = async (key: string, fn: () => Promise<SpagoIntegrationStatus>, fallback: string) => {
    setBusy(key);
    setActionError(null);
    try {
      setStatus(await fn());
      onConnectorsChanged();
    } catch (err) {
      setActionError(errorDetail(err, fallback));
    } finally {
      setBusy(null);
    }
  };

  const handlePackageUpdate = () => runAction(
    'update',
    async () => (await spagoIntegrationAPI.update()).data,
    "Couldn't update the Spago connectors.",
  );

  const handleToggle = (templateKey: string, next: boolean) => runAction(
    `connector:${templateKey}`,
    async () => (await spagoIntegrationAPI.setConnectorActive(templateKey, next)).data,
    "Couldn't change that connector.",
  );

  const handleTurnOffHandmade = () => {
    if (!handmadeTarget) return;
    const target = handmadeTarget;
    void runAction(
      `handmade:${target.id}`,
      async () => (await spagoIntegrationAPI.turnOffHandmade(target.id)).data,
      "Couldn't turn that connector off.",
    );
  };

  const handleDisconnect = async () => {
    const alsoRemoveKey = removeKey;
    setBusy('disconnect');
    setActionError(null);
    try {
      await spagoIntegrationAPI.disconnect(alsoRemoveKey);
      closeForm();
      onConnectorsChanged();
      onIdentityKeysChanged();
      await load();
    } catch (err) {
      setActionError(errorDetail(err, "Couldn't disconnect Spago."));
    } finally {
      setBusy(null);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────

  const header = (
    <div className="flex items-start gap-3 min-w-0">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center shrink-0">
        <Store className="w-5 h-5 text-primary" />
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">Spago</h2>
          {status?.connected && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400">
              <CheckCircle2 className="w-3 h-3" />
              Connected
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Connect Spago to let your agents search products, check stock and answer signed-in shoppers' order questions.
        </p>
      </div>
    </div>
  );

  const form = formMode && (
    <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {formMode === 'connect' ? 'Connect Spago' : 'Replace key'}
        </h3>
        <button
          type="button"
          onClick={closeForm}
          disabled={formSaving}
          aria-label="Close"
          className="p-1 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {formWarnings ? (
        <div className="space-y-3">
          <p className="flex items-center gap-1.5 text-sm font-medium text-green-700 dark:text-green-400">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            {formMode === 'connect' ? 'Spago is connected.' : 'Changes saved.'}
          </p>
          <WarningNotices warnings={formWarnings} />
          <div className="flex sm:justify-end">
            <button type="button" onClick={closeForm} className="btn-primary px-4 py-2 text-sm w-full sm:w-auto">
              Done
            </button>
          </div>
        </div>
      ) : (
        <>
          <ul className="list-disc pl-4 space-y-1 text-xs text-gray-600 dark:text-gray-400">
            {KEY_HELP.map((line) => <li key={line}>{line}</li>)}
          </ul>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Environment</label>
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as SpagoEnvironment)}
              // /update has no environment field — shown for reference only.
              disabled={formMode === 'replace'}
              className="input-field w-full text-sm disabled:opacity-70"
            >
              {environment === '' && <option value="">Choose an environment</option>}
              {(status?.environments ?? []).map((env) => (
                <option key={env.id} value={env.id} disabled={!env.available}>{env.label}</option>
              ))}
            </select>
            {formMode === 'connect' && !(status?.environments ?? []).some((e) => e.available) && (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">No Spago environment is available yet.</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Spago API key</label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={formMode === 'replace' ? 'Leave blank to keep the current key' : 'Paste the API key'}
                autoComplete="new-password"
                spellCheck={false}
                className="input-field w-full text-sm font-mono"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Key ID</label>
              <input
                type="text"
                value={keyId}
                onChange={(e) => setKeyId(e.target.value)}
                placeholder="The API key's ID"
                spellCheck={false}
                className="input-field w-full text-sm font-mono"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Identity secret</label>
            <input
              type="password"
              value={identitySecret}
              onChange={(e) => setIdentitySecret(e.target.value)}
              placeholder={formMode === 'replace' ? 'Leave blank to keep the current key' : 'Paste the identity secret'}
              autoComplete="new-password"
              spellCheck={false}
              className="input-field w-full text-sm font-mono"
            />
            {formMode === 'replace' && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                To change only the agents, leave the API key and identity secret blank.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Buyer agent(s)</label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
              Order and account questions only go to these agents. Use your shopper agent, not your seller agent.
            </p>
            <AgentChecklist agents={agents} selected={buyerIds} onChange={setBuyerIds} />
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Catalog agents</span>
            <div className="flex flex-col sm:flex-row sm:gap-5 gap-1.5 mb-2">
              {(['all', 'selected'] as const).map((val) => (
                <label key={val} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="radio"
                    name="spago-catalog-scope"
                    checked={catalogScope === val}
                    onChange={() => setCatalogScope(val)}
                  />
                  {val === 'all' ? 'All agents' : 'Selected agents'}
                </label>
              ))}
            </div>
            {catalogScope === 'selected' && (
              <AgentChecklist agents={agents} selected={catalogIds} onChange={setCatalogIds} />
            )}
          </div>

          {formError && (
            <p className="text-xs text-red-600 dark:text-red-400 flex items-start gap-1">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
              <span>{formError}</span>
            </p>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              onClick={closeForm}
              disabled={formSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={formSaving || !canSubmit}
              className="btn-primary flex items-center justify-center gap-2 px-4 py-2 text-sm w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : formMode === 'connect' ? <Plug className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {formMode === 'connect' ? 'Connect Spago' : 'Save'}
            </button>
          </div>
        </>
      )}
    </div>
  );

  let body: React.ReactNode;
  if (loading && !status) {
    body = (
      <div className="flex justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  } else if (loadError && !status) {
    body = (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1 flex-1 min-w-0">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span>{loadError}</span>
        </p>
        <button type="button" onClick={load} className="btn-secondary text-xs flex items-center justify-center gap-1.5 w-full sm:w-auto">
          <RefreshCw className="w-3.5 h-3.5" />
          Try again
        </button>
      </div>
    );
  } else if (status && !status.connected) {
    body = (
      <>
        {!formMode && canAdd && (
          <button
            type="button"
            onClick={openConnect}
            className="btn-primary text-sm flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <Plug className="w-4 h-4" />
            Connect Spago
          </button>
        )}
        {form}
      </>
    );
  } else if (status) {
    const catalogSummary = status.catalog_agent_scope === 'all'
      ? 'All agents'
      : status.catalog_agent_ids.map(agentName).join(', ') || '—';
    // Active hand-made Spago connectors, name clashes with a package tool first.
    const activeHandmade = activeHandmadeConnectors(status);
    const warnings = warningsOf(status);
    body = (
      <>
        {/* Actions */}
        {!formMode && (canAdd || canDelete) && (
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
            {canAdd && status.update_available && (
              <button
                type="button"
                onClick={handlePackageUpdate}
                disabled={busy !== null}
                className="btn-primary text-xs flex items-center justify-center gap-1.5 w-full sm:w-auto disabled:opacity-50"
              >
                {busy === 'update' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Update to version {status.latest_version}
              </button>
            )}
            {canAdd && (
              <button
                type="button"
                onClick={openReplace}
                disabled={busy !== null}
                className="btn-secondary text-xs flex items-center justify-center gap-1.5 w-full sm:w-auto disabled:opacity-50"
              >
                <KeyRound className="w-3.5 h-3.5" />
                Replace key
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => { setRemoveKey(false); setConfirmDisconnect(true); }}
                disabled={busy !== null}
                className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800 w-full sm:w-auto sm:ml-auto disabled:opacity-50"
              >
                {busy === 'disconnect' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unplug className="w-3.5 h-3.5" />}
                Disconnect
              </button>
            )}
          </div>
        )}

        {form}

        {/* Summary */}
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div className="min-w-0">
            <dt className="text-xs text-gray-500 dark:text-gray-400">Environment</dt>
            <dd className="text-gray-900 dark:text-gray-100">{environmentLabel(status.environment)}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-gray-500 dark:text-gray-400">Key ID</dt>
            <dd className="text-gray-900 dark:text-gray-100 font-mono text-xs break-all">{status.key_id || '—'}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-gray-500 dark:text-gray-400">Buyer agents</dt>
            <dd className="text-gray-900 dark:text-gray-100 break-words">{status.buyer_agent_ids.map(agentName).join(', ') || '—'}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-gray-500 dark:text-gray-400">Catalog agents</dt>
            <dd className="text-gray-900 dark:text-gray-100 break-words">{catalogSummary}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-gray-500 dark:text-gray-400">Package version</dt>
            <dd className="text-gray-900 dark:text-gray-100">{status.package_version ?? '—'}</dd>
          </div>
        </dl>

        {warnings.length > 0 && <WarningNotices warnings={warnings} />}

        {actionError && (
          <p className="text-xs text-red-600 dark:text-red-400 flex items-start gap-1">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
            <span>{actionError}</span>
          </p>
        )}

        {/* Hand-made connectors that also call Spago */}
        {activeHandmade.length > 0 && (
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 space-y-2">
            <p className="flex gap-2 text-sm text-amber-800 dark:text-amber-300">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>These connectors you created earlier also call Spago. Turn them off to avoid duplicate tools.</span>
            </p>
            <ul className="space-y-2">
              {activeHandmade.map((h) => (
                <li
                  key={h.id}
                  className={`flex flex-col sm:flex-row sm:items-center gap-2 p-2 rounded-md bg-white/70 dark:bg-gray-900/40 ${h.clashesWithPackage ? 'border border-red-300 dark:border-red-800' : ''}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{h.name}</p>
                    <p className="text-xs font-mono text-gray-600 dark:text-gray-400 break-all">{h.endpoint_host}</p>
                    {h.clashesWithPackage && (
                      <p className="mt-1 text-xs font-medium text-red-700 dark:text-red-400 flex items-start gap-1">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                        <span>Same name as a Spago package tool. Turn this off so the package version is used.</span>
                      </p>
                    )}
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => setHandmadeTarget(h)}
                      disabled={busy !== null}
                      className="btn-secondary text-xs flex items-center justify-center gap-1.5 w-full sm:w-auto shrink-0 disabled:opacity-50"
                    >
                      {busy === `handmade:${h.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                      Turn off
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Package connectors */}
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Connectors</h3>
          {status.connectors.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 italic">No connectors installed.</p>
          ) : (
            <ul className="space-y-2">
              {status.connectors.map((c) => {
                const key = `connector:${c.template_key}`;
                const cardLabel = c.render_as === 'product_cards' || c.render_as === 'order_cards'
                  ? CARD_LABELS[c.render_as]
                  : null;
                return (
                  <li key={c.template_key} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{c.title}</p>
                        {c.uses_identity && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                            <UserCheck className="w-3 h-3" />
                            Signed-in shopper
                          </span>
                        )}
                        {cardLabel && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400">
                            <LayoutGrid className="w-3 h-3" />
                            {cardLabel}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{c.summary}</p>
                      <p className="text-[11px] font-mono text-gray-400 dark:text-gray-500 break-all">{c.name}</p>
                      {!c.is_active && c.default_off_reason && (
                        <p className="text-xs text-amber-700 dark:text-amber-400">{c.default_off_reason}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={c.is_active}
                      aria-label={`${c.is_active ? 'Turn off' : 'Turn on'} ${c.title}`}
                      onClick={() => handleToggle(c.template_key, !c.is_active)}
                      disabled={!canEdit || busy !== null}
                      className="shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {busy === key ? (
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      ) : c.is_active ? (
                        <ToggleRight className="w-7 h-7 text-green-500" />
                      ) : (
                        <ToggleLeft className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </>
    );
  }

  return (
    <div className="card p-5 sm:p-6 space-y-4">
      {header}
      {body}

      <ConfirmModal
        isOpen={!!handmadeTarget}
        onClose={() => setHandmadeTarget(null)}
        onConfirm={handleTurnOffHandmade}
        title="Turn Off Connector"
        message={`Turn off "${handmadeTarget?.name}"? Your agents will stop using it. You can turn it back on from the connector list.`}
        confirmText="Turn off"
        cancelText="Cancel"
      />

      <ConfirmModal
        isOpen={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={handleDisconnect}
        title="Disconnect Spago"
        message="Your agents will stop using all Spago connectors. Connectors you created yourself are not changed."
        confirmText="Disconnect"
        cancelText="Cancel"
        isDangerous
      >
        <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            className="rounded mt-0.5 shrink-0"
            checked={removeKey}
            onChange={(e) => setRemoveKey(e.target.checked)}
          />
          <span>Also remove the identity key</span>
        </label>
      </ConfirmModal>
    </div>
  );
};

export default SpagoIntegrationCard;
